import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useEffect, useState } from "react";
import axios from "axios";
import notificationService from "./services/notificationService";
import CardRequiredModal from "./components/CardRequiredModal";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Workout from "./pages/Workout";
import Progress from "./pages/Progress";
import Leaderboard from "./pages/Leaderboard";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import DonationSuccess from "./pages/DonationSuccess";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import Achievements from "./pages/Achievements";
import Referrals from "./pages/Referrals";
import CycleReset from "./pages/CycleReset";
import ShareProgress from "./pages/ShareProgress";
import MeetCoach from "./pages/MeetCoach";
import PhotoCompare from "./pages/PhotoCompare";
import Goals from "./pages/Goals";
import MoodCheckin from "./pages/MoodCheckin";
import Challenges from "./pages/Challenges";
import PartnerWorkouts from "./pages/PartnerWorkouts";
import PartnerWorkout from "./pages/PartnerWorkout";
import PartnerChallenges from "./pages/PartnerChallenges";
import QuitterMode from "./pages/QuitterMode";
import ClassSchedule from "./pages/ClassSchedule";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminContent from "./pages/admin/AdminContent";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminUserProgress from "./pages/admin/AdminUserProgress";
import AdminProfileViews from "./pages/admin/AdminProfileViews";
import AdminAffiliates from "./pages/admin/AdminAffiliates";
import AdminPayouts from "./pages/admin/AdminPayouts";
import AdminEmails from "./pages/admin/AdminEmails";
import "./App.css";

// Initialize Push Notifications
const initNotifications = async () => {
  try {
    const initialized = await notificationService.init();
    if (initialized) {
      console.log('[App] Push notification service initialized');
    }
  } catch (error) {
    console.log('[App] Push notifications not available:', error.message);
  }
};

const API = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children, skipPaymentCheck = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingPayment, setCheckingPayment] = useState(true);
  const [requiresPayment, setRequiresPayment] = useState(false);
  
  // Routes that should skip payment check (payment flow itself)
  const paymentExemptRoutes = ['/payment/success', '/onboarding', '/pricing'];
  const isPaymentExempt = paymentExemptRoutes.some(route => location.pathname.startsWith(route)) || skipPaymentCheck;
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (isPaymentExempt || !user) {
        setCheckingPayment(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/api/subscription/status`);
        setRequiresPayment(response.data.requires_payment_method);
      } catch (error) {
        console.error("Payment check failed:", error);
      } finally {
        setCheckingPayment(false);
      }
    };
    
    if (!loading && user) {
      checkPaymentStatus();
    } else if (!loading) {
      setCheckingPayment(false);
    }
  }, [user, loading, isPaymentExempt]);
  
  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // MANDATORY: Show card required modal if no payment method
  if (requiresPayment && !isPaymentExempt) {
    return (
      <div className="min-h-screen bg-background">
        <CardRequiredModal />
      </div>
    );
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  // Initialize push notifications on app load
  useEffect(() => {
    initNotifications();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/schedule" element={<ClassSchedule />} />
      <Route path="/meet-coach" element={<MeetCoach />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/donation-success" element={<DonationSuccess />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/workout/:day" element={<ProtectedRoute><Workout /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/cycle" element={<ProtectedRoute><CycleReset /></ProtectedRoute>} />
      <Route path="/share" element={<ProtectedRoute><ShareProgress /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><PhotoCompare /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
      <Route path="/mood" element={<ProtectedRoute><MoodCheckin /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
      <Route path="/partner-workouts" element={<ProtectedRoute><PartnerWorkouts /></ProtectedRoute>} />
      <Route path="/partner-workout/:workoutId" element={<ProtectedRoute><PartnerWorkout /></ProtectedRoute>} />
      <Route path="/partner-challenges" element={<ProtectedRoute><PartnerChallenges /></ProtectedRoute>} />
      <Route path="/quitter-mode" element={<ProtectedRoute><QuitterMode /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/users/:userId/progress" element={<AdminUserProgress />} />
      <Route path="/admin/content" element={<AdminContent />} />
      <Route path="/admin/announcements" element={<AdminAnnouncements />} />
      <Route path="/admin/payments" element={<AdminPayments />} />
      <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
      <Route path="/admin/profile-views" element={<AdminProfileViews />} />
      <Route path="/admin/affiliates" element={<AdminAffiliates />} />
      <Route path="/admin/payouts" element={<AdminPayouts />} />
      <Route path="/admin/emails" element={<AdminEmails />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
